import { useMutation } from "@tanstack/react-query";
import clsx from "clsx";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Team, UserAttributes } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { Icon } from "~/assets/icons/Icon";
import { useAuth } from "~/contexts/AuthProvider";
import { getLogoPlaceholderUrl } from "~/helpers/logoPlaceholder";
import { useImpersonation } from "~/hooks/useImpersonation";
import { useTeams } from "~/hooks/useTeams";
import { routes } from "~/router";

import { Option, Select } from "../../ui/select/Select";
import { CreateNewTeamModal } from "../create_new_team_modal/CreateNewTeamModal";
import style from "./TeamSelect.module.scss";

const ADD_TEAM_SPECIAL_VALUE = "--add-team--";

export const TeamSelect = ({ isSidebarOpen }: { isSidebarOpen: boolean }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isImpersonating } = useImpersonation();

  const { user, refetchUser } = useAuth();
  const {
    data: teams = [],
    isFetching: isTeamsLoading,
    refetch: refetchTeams,
  } = useTeams();

  const { mutateAsync, isPending: isSaving } = useMutation({
    mutationFn: async (data: UserAttributes) => {
      await apiClient.user.userPartialUpdate(data);
    },
    onSuccess: async () => {
      await Promise.all([refetchUser(), refetchTeams()]);
      navigate(routes.home());
    },
  });

  if (!user) {
    return null;
  }

  const handleTeamChange = async (teamId: string) => {
    await mutateAsync({
      current_team_id: Number(teamId),
      email: user.email || "",
      first_name: user.first_name,
      last_name: user.last_name,
    });

    if (location.pathname.includes("site")) {
      navigate(routes.home(), { replace: true });
    }
  };

  const currentTeam = teams.find(
    (team) => Number(team.id) === Number(user.current_team_id),
  );

  return (
    <>
      <Select
        disabled={isTeamsLoading || isSaving}
        multiple={false}
        value={currentTeam ? String(currentTeam.id) : ""}
        onChange={(_evt, value) => {
          if (value === ADD_TEAM_SPECIAL_VALUE) {
            setModalOpen(true);

            return;
          }

          if (value) {
            handleTeamChange(value);
          }
        }}
        className={isSidebarOpen ? style.select : style.selectClosed}
        slotProps={{
          root: { className: clsx({ [style.buttonDanger]: isImpersonating }) },
        }}
        renderValue={(option) => {
          const selectedTeam = teams.find(
            (team) => String(team.id) === option?.value,
          );

          if (!selectedTeam) {
            return null;
          }

          return (
            <TeamBadge
              team={selectedTeam}
              variant={isSidebarOpen ? "full" : "short"}
            />
          );
        }}
      >
        {teams.map((team) => (
          <Option key={team.id} value={String(team.id)}>
            <span className={style.optionContent}>
              <TeamBadge team={team} variant="full" />
            </span>
          </Option>
        ))}
        <Option value={ADD_TEAM_SPECIAL_VALUE}>
          <span className={style.optionContent}>
            <span className={style.avatar}>
              <Icon iconName="PlusIcon" color="#7a5af8" />
            </span>
            <b>Create new team</b>
          </span>
        </Option>
      </Select>
      <CreateNewTeamModal
        isOpen={modalOpen}
        closeModal={() => setModalOpen(false)}
        onTeamCreated={(newTeam) => {
          handleTeamChange(String(newTeam.id));
        }}
      />
    </>
  );
};

const TeamBadge = ({
  team,
  variant,
}: {
  team: Team;
  variant: "short" | "full";
}) => {
  return (
    <span className={style.teamWrapper}>
      <TeamAvatar team={team} />
      {variant === "full" && (
        <span className={style.teamName}>{team.name}</span>
      )}
    </span>
  );
};

const TeamAvatar = ({ team }: { team: Team }) => {
  const logoUrl = team?.logo?.url || getLogoPlaceholderUrl(team);

  return (
    <span>
      {logoUrl ? (
        <img src={logoUrl} alt={team.name} className={style.avatarImage} />
      ) : (
        <span className={style.avatar}>{team?.name?.slice(0, 1)}</span>
      )}
    </span>
  );
};
